/* ---
	Home hero — Swiper stack

	Vertical creative Swiper (same pattern as ysl.com).

	Desktop: custom wheel handler pins the page and advances one slide per
	gesture. Last slide + scroll down (or first + up) releases into normal page
	scroll.

	Mobile: Swiper owns touch. While not on the last slide, the page is scroll-
	locked so the swipe cannot be stolen by the document. On the last slide the
	lock lifts so the rest of the page can scroll.
--- */

if (typeof initHeroSwiperStack !== 'function') {
  const BASE_SPEED_MS = 500;
  const PIN_TOLERANCE = 12;

  function heroTransitionSpeed(percent) {
    const p = parseFloat(percent) || 100;
    return Math.round((BASE_SPEED_MS * 100) / p);
  }

  function sectionEl(root) {
    return root.closest('.kt-hero-section') || root.closest('.kt-hero') || root;
  }

  function pinY(root) {
    const section = sectionEl(root);
    return section.getBoundingClientRect().top + window.scrollY;
  }

  function sectionTop(root) {
    return sectionEl(root).getBoundingClientRect().top;
  }

  function isPinned(root) {
    const top = sectionTop(root);
    return top > -PIN_TOLERANCE && top < PIN_TOLERANCE;
  }

  /* Hero fills enough of the viewport to own gestures. */
  function isHeroActive(root) {
    const rect = sectionEl(root).getBoundingClientRect();
    return rect.top < window.innerHeight * 0.55 && rect.bottom > window.innerHeight * 0.45;
  }

  function lockScrollToHero(root) {
    const y = pinY(root);
    if (Math.abs(window.scrollY - y) > 1) {
      window.scrollTo(0, y);
    }
  }

  function shouldCapture(swiper, deltaY) {
    if (!swiper || swiper.destroyed) return false;
    if (deltaY > 0) return !swiper.isEnd;
    if (deltaY < 0) return !swiper.isBeginning;
    return false;
  }

  function initHeroSwiper(root) {
    if (!root || root.swiper || typeof Swiper === 'undefined') return null;

    const slides = root.querySelectorAll('.swiper-slide');
    if (slides.length < 2) return null;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarse =
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
      (navigator.maxTouchPoints > 1 && window.matchMedia('(hover: none)').matches);
    const speed = reduced ? 0 : heroTransitionSpeed(root.dataset.heroSwiperSpeed);
    let locking = false;
    let gestureCooldown = false;

    /*
      One slide per physical gesture. Trackpad inertia keeps firing wheel
      events long after the flick — long enough to outlive the transition
      cooldown and skip several slides. A gesture is consumed on advance
      and only re-arms after a quiet gap; the tail of the same gesture
      never re-triggers because it has no such gap.
    */
    const WHEEL_REARM_MS = 200;
    const WHEEL_THRESHOLD = 40;
    let wheelArmed = true;
    let wheelAcc = 0;
    let lastWheelAt = 0;
    let lastWheelDelta = 0;
    let lastAdvanceAt = 0;

    const swiper = new Swiper(root, {
      direction: 'vertical',
      effect: 'creative',
      speed,
      slidesPerView: 1,
      spaceBetween: 0,
      watchSlidesProgress: true,
      resistanceRatio: 0.85,
      threshold: coarse ? 10 : 5,
      longSwipesRatio: 0.2,
      followFinger: true,
      /* Mobile: Swiper handles swipes. Desktop: we drive slides from wheel. */
      allowTouchMove: !reduced,
      touchReleaseOnEdges: true,
      simulateTouch: true,
      mousewheel: {
        enabled: false,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      creativeEffect: {
        /* Flat Y motion only — Z/perspective reads as a zoom on mobile GPUs. */
        perspective: false,
        limitProgress: 1,
        prev: {
          /* ysl.com: the outgoing slide drifts up behind the incoming one
             (parallax), and slides back down in reverse. */
          opacity: 1,
          translate: [0, '-25%', 0],
          scale: 1,
        },
        next: {
          opacity: 1,
          translate: [0, '100%', 0],
          scale: 1,
        },
      },
    });

    const setScrollLock = (lock) => {
      document.documentElement.classList.toggle('kt-hero-scroll-lock', lock);
    };

    const holdPage = () => {
      locking = true;
      setScrollLock(true);
      /* scrollTo during touch fights iOS and looks like a zoom/jank hitch. */
      if (!coarse) lockScrollToHero(root);
    };

    const releasePage = () => {
      locking = false;
      setScrollLock(false);
    };

    /*
      Mobile page lock: keep the document from scrolling until the last slide.
      Without this, iOS/Android steal the swipe and the next slide never arrives.
    */
    const syncMobileLock = () => {
      if (reduced || !coarse) return;
      if (!isHeroActive(root)) {
        setScrollLock(false);
        locking = false;
        return;
      }
      if (swiper.animating || !swiper.isEnd) {
        holdPage();
      } else {
        releasePage();
      }
    };

    const advance = (deltaY) => {
      if (swiper.animating || gestureCooldown) return false;
      if (!shouldCapture(swiper, deltaY)) return false;
      gestureCooldown = true;
      holdPage();
      if (deltaY > 0) swiper.slideNext();
      else swiper.slidePrev();
      window.setTimeout(
        () => {
          gestureCooldown = false;
        },
        Math.max(speed, 160),
      );
      return true;
    };

    const onWheel = (event) => {
      if (reduced || coarse) return;

      const deltaY = event.deltaY;
      if (!deltaY) return;

      const now = event.timeStamp || performance.now();
      const absDelta = Math.abs(deltaY);
      if (now - lastWheelAt > WHEEL_REARM_MS) {
        /* Quiet gap: definitely a new gesture. */
        wheelArmed = true;
        wheelAcc = 0;
      } else if (
        !wheelArmed &&
        absDelta > 12 &&
        absDelta > Math.abs(lastWheelDelta) * 1.4 &&
        now - lastAdvanceAt > speed + 120
      ) {
        /* Inertia only ever decays — a rising delta inside the stream is a
           fresh flick layered on the old tail. Without this, flicking again
           while the previous tail is still trickling gets swallowed and the
           hero feels unresponsive (3→2→1 not reacting). The advance guard
           keeps the ramp-up of a single flick from double-firing. */
        wheelArmed = true;
        wheelAcc = 0;
      }
      lastWheelAt = now;
      lastWheelDelta = deltaY;

      const top = sectionTop(root);
      if (!isHeroActive(root)) {
        releasePage();
        return;
      }

      if (top > PIN_TOLERANCE) {
        releasePage();
        return;
      }

      if (top < -PIN_TOLERANCE) {
        /* Below the hero: never grab the wheel. Snapping the page back to
           the pin here read as an abrupt slide switch — instead the page
           scrolls up naturally and the slides only reverse (animated)
           once the hero is pinned again. */
        releasePage();
        return;
      }

      if (swiper.animating) {
        event.preventDefault();
        holdPage();
        return;
      }

      if (!shouldCapture(swiper, deltaY)) {
        releasePage();
        return;
      }

      event.preventDefault();

      /* Inertia from an already-consumed gesture: keep the page pinned
         but never feed it into another slide. */
      if (!wheelArmed) return;

      wheelAcc += deltaY;
      if (Math.abs(wheelAcc) < WHEEL_THRESHOLD) return;

      if (advance(deltaY)) {
        wheelArmed = false;
        wheelAcc = 0;
        lastAdvanceAt = now;
      }
    };

    const onScroll = () => {
      if (reduced) return;
      if (coarse) {
        syncMobileLock();
        return;
      }
      if (!locking) return;
      if (swiper.animating || (!swiper.isBeginning && !swiper.isEnd)) {
        lockScrollToHero(root);
        return;
      }
      releasePage();
    };

    const onKey = (event) => {
      if (reduced || coarse) return;
      if (!isPinned(root) && sectionTop(root) > PIN_TOLERANCE) return;

      const down =
        event.key === 'ArrowDown' ||
        event.key === 'PageDown' ||
        (event.code === 'Space' && !event.shiftKey);
      const up =
        event.key === 'ArrowUp' ||
        event.key === 'PageUp' ||
        (event.code === 'Space' && event.shiftKey);
      if (!down && !up) return;
      if (event.target && /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;

      const deltaY = down ? 1 : -1;
      if (swiper.animating) {
        event.preventDefault();
        holdPage();
        return;
      }
      if (!shouldCapture(swiper, deltaY)) {
        releasePage();
        return;
      }
      event.preventDefault();
      advance(deltaY);
    };

    swiper.on('slideChangeTransitionStart', () => {
      if (coarse) holdPage();
      else holdPage();
    });

    swiper.on('slideChangeTransitionEnd', () => {
      if (coarse) {
        syncMobileLock();
        return;
      }
      if (swiper.isBeginning || swiper.isEnd) {
        lockScrollToHero(root);
        releasePage();
      } else {
        holdPage();
      }
    });

    swiper.on('touchStart', () => {
      if (!coarse || reduced) return;
      if (isHeroActive(root) && !swiper.isEnd) holdPage();
    });

    swiper.on('touchEnd', () => {
      if (!coarse || reduced) return;
      window.setTimeout(syncMobileLock, 0);
    });

    swiper.on('reachEnd', () => {
      if (coarse) releasePage();
    });

    swiper.on('fromEdge', () => {
      if (coarse && isHeroActive(root)) holdPage();
    });

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', syncMobileLock, { passive: true });

    root._heroSwiperCleanup = () => {
      releasePage();
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', syncMobileLock);
    };

    syncMobileLock();
    return swiper;
  }

  function initHeroSwiperStack(scope = document) {
    scope.querySelectorAll('[data-hero-swiper]').forEach((root) => {
      initHeroSwiper(root);
    });
  }

  function bootHeroSwiperStack() {
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', bootHeroSwiperStack, { once: true });
      return;
    }
    initHeroSwiperStack();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootHeroSwiperStack);
  } else {
    bootHeroSwiperStack();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target.querySelector('[data-hero-swiper]')) return;
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => initHeroSwiperStack(event.target), { once: true });
      return;
    }
    initHeroSwiperStack(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-hero-swiper]').forEach((root) => {
      if (typeof root._heroSwiperCleanup === 'function') root._heroSwiperCleanup();
      if (root.swiper) root.swiper.destroy(true, true);
    });
  });
}
